import * as THREE from 'three'
import gsap from 'gsap'
import type { SceneGraph } from './SceneGraph'

/**
 * Particles
 *
 * High-end read:
 * - depth via per-particle size + alpha variation
 * - near motes drift slightly faster (parallax cue, not “screensaver”)
 * - soft sprite (blur), not hard dots
 */
export class Particles {
  private points: THREE.Points
  private uniforms: {
    uTime: { value: number }
    uIntensity: { value: number }
    uDrift: { value: number }
    uSize: { value: number }
  }

  constructor(graph: SceneGraph) {
    const COUNT = 750 // performance knob

    const positions = new Float32Array(COUNT * 3)
    const aScale = new Float32Array(COUNT) // size variation (depth cue)
    const aAlpha = new Float32Array(COUNT) // opacity variation (depth cue)
    const aSpeed = new Float32Array(COUNT) // drift speed (near feels faster)

    // A loose “air volume” around the blob
    // We bias a bit toward “far field” for calm density, with a few near big motes.
    for (let i = 0; i < COUNT; i++) {
      const t = Math.random()

      // Radius: mostly mid/far, not all glued to center
      const r = THREE.MathUtils.lerp(0.30, 1.15, Math.pow(t, 0.85))

      // Angle around Y axis
      const a = Math.random() * Math.PI * 2

      // Vertical spread with slight center bias
      const y = THREE.MathUtils.lerp(-0.75, 0.85, Math.pow(Math.random(), 0.9))

      // Add a little extra depth variation beyond perfect ring symmetry
      const zJitter = THREE.MathUtils.lerp(-0.35, 0.35, Math.random())

      positions[i * 3 + 0] = Math.cos(a) * r
      positions[i * 3 + 1] = y
      positions[i * 3 + 2] = Math.sin(a) * r + zJitter

      // Depth “layer” controls scale, alpha, speed.
      // Bigger ones should feel closer, and yes, a touch faster reads as “near”.
      const layer = Math.random()
      aScale[i] = THREE.MathUtils.lerp(0.55, 2.10, Math.pow(layer, 1.35))
      aAlpha[i] = THREE.MathUtils.lerp(0.18, 1.00, Math.pow(1.0 - layer, 0.65))
      aSpeed[i] = THREE.MathUtils.lerp(0.25, 1.05, Math.pow(layer, 1.25))
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aScale', new THREE.BufferAttribute(aScale, 1))
    geo.setAttribute('aAlpha', new THREE.BufferAttribute(aAlpha, 1))
    geo.setAttribute('aSpeed', new THREE.BufferAttribute(aSpeed, 1))

    this.uniforms = {
      uTime: { value: 0 },
      uIntensity: { value: 0.0 }, // start off
      uDrift: { value: 0.06 },
      uSize: { value: 29.0 } // base size, per-particle aScale does the depth work
    }

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: this.uniforms,
      vertexShader: `
        uniform float uTime;
        uniform float uIntensity;
        uniform float uDrift;
        uniform float uSize;

        attribute float aScale;
        attribute float aAlpha;
        attribute float aSpeed;

        varying float vAlpha;
        varying float vSoft;

        // tiny hash noise
        float hash(vec3 p){
          p = fract(p * 0.3183099 + 0.1);
          p *= 17.0;
          return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
        }

        void main() {
          vec3 p = position;

          // per-particle time makes the field feel layered
          float t = uTime * aSpeed;

          float n1 = hash(p + t * 0.07);
          float n2 = hash(p.yzx + t * 0.05);
          float n3 = hash(p.zxy + t * 0.06);

          // gentle, non-swirly drift
          p.y += (n1 - 0.5) * uDrift;
          p.x += (n2 - 0.5) * (uDrift * 0.35);
          p.z += (n3 - 0.5) * (uDrift * 0.35);

          // Fade: global intensity, per-particle alpha, and distance from center
          float r = length(p.xz);
          float centerFalloff = smoothstep(1.35, 0.20, r);
          vAlpha = uIntensity * aAlpha * centerFalloff;

          // Extra softness for larger motes so “big” reads “blurry”, not “loud”
          vSoft = mix(1.15, 0.75, clamp((aScale - 0.55) / 1.55, 0.0, 1.0));

          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;

          // Screen-space scaling with depth
          float depthScale = (1.0 / max(0.6, -mv.z));
          gl_PointSize = (uSize * aScale) * depthScale;
        }
      `,
      fragmentShader: `
        precision highp float;
        varying float vAlpha;
        varying float vSoft;

        void main() {
          // map to -1..1
          vec2 uv = gl_PointCoord * 2.0 - 1.0;
          float d2 = dot(uv, uv);

          // gaussian-ish soft blob
          float a = exp(-d2 * (3.25 * vSoft));
          a *= vAlpha;

          // gently trim edges so additive doesn't “salt” the frame
          a *= smoothstep(1.0, 0.85, 1.0 - d2);

          gl_FragColor = vec4(1.0, 1.0, 1.0, a);
        }
      `
    })

    this.points = new THREE.Points(geo, mat)
    this.points.frustumCulled = false
    graph.scene.add(this.points)
  }

  update(dt: number) {
    this.uniforms.uTime.value += dt
  }

  setPhase(phaseId: string, d: number) {
    // Targets tuned for “bigger + blurrier + editorial”.
    // Hold is a bit denser and larger, inhale/exhale slightly lighter.
    const inhale = { intensity: 0.45, drift: 0.30, size: 16.0 }
    const hold   = { intensity: 0.65, drift: 0.18, size: 16.0 }
    const exhale = { intensity: 0.45, drift: 0.26, size: 16.0 }

    const t =
      phaseId === 'inhale' ? inhale :
      phaseId === 'hold'   ? hold   : exhale

    gsap.to(this.uniforms.uIntensity, { value: t.intensity, duration: Math.min(0.6, d * 0.35), ease: 'sine.inOut', overwrite: true })
    gsap.to(this.uniforms.uDrift,     { value: t.drift,     duration: Math.min(0.8, d * 0.45), ease: 'sine.inOut', overwrite: true })
    gsap.to(this.uniforms.uSize,      { value: t.size,      duration: 0.7,                   ease: 'sine.inOut', overwrite: true })
  }
}
